import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Text,
  useToast,
  Box,
  Icon,
  useColorModeValue,
  Select,
  Switch,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Textarea,
  Divider,
  Badge,
} from '@chakra-ui/react';
import { FiPlus, FiCheck, FiInfo } from 'react-icons/fi';
import { useForm } from 'react-hook-form';
import LocationPicker, { LocationData } from '@/components/common/LocationPicker';
import apiClient, { Tenant } from '@/services/letsparkyClient';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from 'react-i18next';

interface AddDeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface CreateDeviceFormData {
  serial_no: string;
  nick_name?: string;
  type?: string;
  terminal_armon_id?: string;
  firmware_version?: string;
  photo?: string;
  imei_number?: string;
  mac_address?: string;
  ble_password?: string;
  geo_lat?: number;
  geo_lng?: number;
  location?: string;
  description?: string;
  is_public?: boolean;
  tenant_id?: string;
}

const AddDeviceModal: React.FC<AddDeviceModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const toast = useToast();
  const { token, user } = useAuthStore();
  const isSystem = user?.role === 'SYSTEM';
  const [isSaving, setIsSaving] = useState(false);
  const [locationData, setLocationData] = useState<LocationData | null>(null);
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const { t } = useTranslation();

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("blue.50", "blue.900");
  const publicDeviceBg = useColorModeValue("blue.50", "blue.900");

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateDeviceFormData>({
    defaultValues: {
      is_public: false,
      tenant_id: isSystem ? undefined : user?.tenantId,
    }
  });

  const isPublicDevice = watch('is_public');
  // const currency = watch('currency');
  // const hourly_rate = watch('hourly_rate');
  // const first_hour_rate = watch('first_hour_rate');
  // const max_daily_rate = watch('max_daily_rate');
  // const free_minutes = watch('free_minutes');

  // const calculateFee = (hours: number) => {
  //   const final_hours = Math.max(hours - (free_minutes ? free_minutes / 60 : 0), 0)
  //   if (final_hours <= 0) return 0;
  //   if (final_hours >= 24) {
  //     const days = final_hours / 24;
  //     if (max_daily_rate) {
  //       return days * max_daily_rate;
  //     }
  //   }
  //   if (final_hours > 0) {
  //     if (first_hour_rate) {
  //       return first_hour_rate + (final_hours > 1? (final_hours - 1) * (hourly_rate || 0) : 0)
  //     } else {
  //       return final_hours * (hourly_rate || 0);
  //     }
  //   }
  // }

  useEffect(() => {
    if (isOpen) {
      if (isSystem) {
        fetchAvailableTenants();
      }
      // Reset form when modal opens
      reset({
        is_public: false,
        tenant_id: isSystem ? undefined : user?.tenantId,
      });
      setLocationData(null);
    }
  }, [isOpen, reset, isSystem, user?.tenantId]);

  const fetchAvailableTenants = async () => {
    if (!token || !isSystem) return;
    setIsLoadingTenants(true);
    try {
      apiClient.client.setToken(token);
      const response = await apiClient.tenants.list({ limit: 1000 });
      if (response && response.data && Array.isArray(response.data.data)) {
        setAvailableTenants(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
      toast({
        title: 'Error loading tenants',
        description: 'Could not load available tenants',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingTenants(false);
    }
  };

  const handleLocationChange = (location: LocationData) => {
    setLocationData(location);
    setValue('geo_lat', location.latitude);
    setValue('geo_lng', location.longitude);
    setValue('location', location.address);
  };

  const onSubmit = async (data: CreateDeviceFormData) => {
    setIsSaving(true);
    try {
      if (!token) throw new Error('Authentication token not available');
      apiClient.client.setToken(token);

      // Prepare the request data
      const requestData = {
        ...data,
        geo_lat: locationData?.latitude,
        geo_lng: locationData?.longitude,
        location: locationData?.address || data.location,
      };

      await apiClient.devices.create(requestData);
      
      toast({
        title: 'Device created successfully',
        description: `Device ${data.nick_name || data.serial_no} has been created`,
        status: 'success',
        duration: 4000,
      });
      
      onSuccess?.();
      onClose();
    } catch (error) {
      toast({
        title: 'Failed to create device',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent maxHeight="90vh">
        <ModalHeader bg={headerBg} borderTopRadius="md">
          <HStack spacing={3}>
            <Icon as={FiPlus} boxSize={5} color="blue.500" />
            <VStack align="start" spacing={0}>
              <Text>Add New Device</Text>
              <Text fontSize="sm" fontWeight="normal" color="gray.600">
                Create a new parking device in the system
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody p={6}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <VStack spacing={6} align="stretch">
              {/* Basic Information */}
              <Box
                p={4}
                borderWidth="1px"
                borderRadius="md"
                borderColor={borderColor}
                bg={cardBg}
              >
                <Text fontWeight="medium" mb={4} color="gray.700">
                  <Icon as={FiInfo} mr={2} />
                  Basic Information
                </Text>
                <VStack spacing={4}>
                  <HStack spacing={4} width="100%">

                    <FormControl flex={1}>
                      <FormLabel>Device Name</FormLabel>
                      <Input
                        {...register('nick_name')}
                        placeholder="Enter a friendly name for the device"
                      />
                    </FormControl>
                  </HStack>

                  <HStack spacing={4} width="100%">
                    <FormControl flex={1}>
                      <FormLabel>Type</FormLabel>
                      <Select {...register('type')} defaultValue="PARKY_TCP">
                        <option value="PARKY_TCP">PARKY_TCP</option>
                        <option value="PARKY_BLUETOOTH">PARKY_BLUETOOTH</option>
                        <option value="TERMINAL">TERMINAL</option>
                        <option value="QR_FLAG">QR_FLAG</option>
                      </Select>
                    </FormControl>
                  </HStack>

                  <FormControl flex={1}>
                      <FormLabel>Photo URL</FormLabel>
                      <Input
                        {...register('photo')}
                        placeholder="URL to device photo"
                      />
                    </FormControl>

                  <FormControl>
                    <FormLabel>Description</FormLabel>
                    <Textarea
                      {...register('description')}
                      placeholder="Optional description for the device"
                      rows={3}
                    />
                  </FormControl>
                </VStack>
              </Box>

              {/* Technical Information */}
              <Box
                p={4}
                borderWidth="1px"
                borderRadius="md"
                borderColor={borderColor}
                bg={cardBg}
              >
                <Text fontWeight="medium" mb={4} color="gray.700">
                  Technical Information
                </Text>
                <VStack spacing={4}>
                  <HStack spacing={4} width="100%">
                    <FormControl isInvalid={!!errors.serial_no} isRequired flex={1}>
                      <FormLabel>Serial Number</FormLabel>
                      <Input
                        {...register('serial_no', {
                          required: 'Serial number is required',
                        })}
                        placeholder="Enter device serial number"
                      />
                      <FormErrorMessage>
                        {errors.serial_no?.message}
                      </FormErrorMessage>
                    </FormControl>
                    <FormControl flex={1}>
                      <FormLabel>Terminal Armon ID</FormLabel>
                      <Input
                        {...register('terminal_armon_id')}
                        placeholder="Terminal Armon ID"
                      />
                    </FormControl>
                  </HStack>
                  <HStack spacing={4} width="100%">
                    <FormControl flex={1}>
                      <FormLabel>IMEI Number</FormLabel>
                      <Input
                        {...register('imei_number')}
                        placeholder="Device IMEI number"
                      />
                    </FormControl>

                    <FormControl flex={1}>
                      <FormLabel>MAC Address</FormLabel>
                      <Input
                        {...register('mac_address')}
                        placeholder="Device MAC address"
                      />
                    </FormControl>
                  </HStack>

                  <HStack spacing={4} width="100%">
                                      
                    <FormControl flex={1}>
                      <FormLabel>BLE Password</FormLabel>
                      <Input
                        {...register('ble_password')}
                        placeholder="BLE password"
                        type="password"
                      />
                    </FormControl>
                 

                    <FormControl flex={1}>
                      <FormLabel>Firmware Version</FormLabel>
                      <Input
                        {...register('firmware_version')}
                        placeholder="Firmware version"
                      />
                    </FormControl>
                  </HStack>

                </VStack>
              </Box>

              {/* Device Settings */}
              <Box
                p={4}
                borderWidth="1px"
                borderRadius="md"
                borderColor={borderColor}
                bg={cardBg}
              >
                <Text fontWeight="medium" mb={4} color="gray.700">
                  Device Settings
                </Text>
                <VStack spacing={4}>
                  <FormControl display="flex" alignItems="center">
                    <FormLabel htmlFor="is_public" mb="0">
                      Public Device
                    </FormLabel>
                    <Switch
                      id="is_public"
                      {...register('is_public')}
                    />
                  </FormControl>

                  {/* {isPublicDevice && (
                    <Box
                      p={4}
                      borderWidth="1px"
                      borderRadius="md"
                      borderColor="blue.200"
                      bg={publicDeviceBg}
                      width="100%"
                    >
                      <Text fontWeight="medium" mb={4} color="blue.700">
                        Public Device Rental Settings
                      </Text>
                      <VStack spacing={4}>
                        <HStack spacing={4} width="100%">
                          <FormControl flex={1}>
                            <FormLabel>Currency</FormLabel>
                            <Select {...register('currency')}>
                              <option value="USD">USD</option>
                              <option value="EUR">EUR</option>
                              <option value="TRY">TRY</option>
                              <option value="GBP">GBP</option>
                            </Select>
                          </FormControl>

                          <FormControl flex={1}>
                            <FormLabel>Hourly Rate</FormLabel>
                            <NumberInput>
                              <NumberInputField
                                {...register('hourly_rate', {
                                  valueAsNumber: true,
                                })}
                                placeholder="50"
                                required={true}
                              />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </FormControl>

                          <FormControl flex={1}>
                            <FormLabel>First Hour Rate</FormLabel>
                            <NumberInput>
                              <NumberInputField
                                {...register('first_hour_rate', {
                                  valueAsNumber: true,
                                })}
                                placeholder="100"
                              />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </FormControl>

                          <FormControl flex={1}>
                            <FormLabel>Max Daily Rate</FormLabel>
                            <NumberInput>
                              <NumberInputField
                                {...register('max_daily_rate', {
                                  valueAsNumber: true,
                                })}
                                placeholder="300"
                              />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </FormControl>

                          <FormControl flex={1}>
                            <FormLabel>Free Minutes</FormLabel>
                            <NumberInput>
                              <NumberInputField
                                {...register('free_minutes', {
                                  valueAsNumber: true,
                                })}
                                placeholder="30"
                              />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </FormControl>
                        </HStack>
                      </VStack>
                      <div style={{ textAlign: 'left', width: '100%', margin: '20px 0 -6px 5px', fontSize: '12px', color: '#555' }}>
                        1.5 hours = First hour rate + 0.5 * Hourly rate ~= { calculateFee(1.5) } {currency}<br/>
                        1.5 days = 1.5 * Max daily rate or 36 hour rate ~= { calculateFee(36) } {currency}<br/>
                      </div>
                    </Box>
                  )} */}
                </VStack>
              </Box>

              {/* Location Information */}
              <Box
                p={4}
                borderWidth="1px"
                borderRadius="md"
                borderColor={borderColor}
                bg={cardBg}
              >
                <Text fontWeight="medium" mb={4} color="gray.700">
                  Location Information
                </Text>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>Device Location</FormLabel>
                    <LocationPicker
                      value={locationData || undefined}
                      onChange={handleLocationChange}
                      placeholder="Search for device location or click on map"
                      height="250px"
                    />
                  </FormControl>

                  {/* <FormControl>
                    <FormLabel>Location Description</FormLabel>
                    <Input
                      {...register('location')}
                      placeholder="Additional location description"
                      value={locationData?.address || ''}
                      onChange={(e) => {
                        setValue('location', e.target.value);
                      }}
                    />
                  </FormControl> */}
                </VStack>
              </Box>

              {/* Tenant Assignment (System users only) */}
              {isSystem && (
                <Box
                  p={4}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor={borderColor}
                  bg={cardBg}
                >
                  <Text fontWeight="medium" mb={4} color="gray.700">
                    Tenant Assignment
                  </Text>
                  <FormControl>
                    <FormLabel>Assign to Tenant</FormLabel>
                    <Select
                      {...register('tenant_id')}
                      placeholder="Select a tenant (optional)"
                      isDisabled={isLoadingTenants}
                    >
                      {availableTenants.map((tenant) => (
                        <option key={tenant.id} value={tenant.id}>
                          {tenant.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </VStack>
          </form>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor={borderColor}>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose} isDisabled={isSaving}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit(onSubmit)}
              isLoading={isSaving}
              loadingText="Creating Device..."
              leftIcon={<Icon as={FiCheck} />}
            >
              Create Device
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddDeviceModal;